import { Mail, MessageSquare, User, MapPin, Globe, Instagram, Bell, Phone } from 'lucide-react';

interface Prospect {
  id: string;
  name: string;
  address: string;
  contact_name: string | null;
  contact_role: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  last_contacted_at: string | null;
  created_at: string;
  updated_at: string;
  type: string | null;
  website: string | null;
  instagram: string | null;
}

interface ProspectCardProps {
  prospect: Prospect;
  onClick: () => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
}

const getDaysSince = (date: string | null): number | null => {
  if (!date) return null;
  const diff = Date.now() - new Date(date).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const getRecencyColor = (days: number | null): string => {
  if (days === null) return 'bg-muted text-muted-foreground';
  if (days <= 3) return 'bg-emerald-100 text-emerald-700';
  if (days <= 7) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const getRecencyLabel = (days: number | null): string => {
  if (days === null) return 'Not contacted';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days}d ago`;
};

const getFollowUpRecommendation = (prospect: Prospect): { show: boolean; method: string; icon: typeof Mail } | null => {
  const days = getDaysSince(prospect.last_contacted_at);
  if (days === null || days < 3) return null;
  // Don't show for won/lost
  if (prospect.status === 'won' || prospect.status === 'lost') return null;

  // Recommend method based on available channels
  if (days >= 5 && prospect.contact_phone) return { show: true, method: 'Phone', icon: Phone };
  if (days >= 4 && prospect.instagram) return { show: true, method: 'Instagram', icon: Instagram };
  if (prospect.contact_email) return { show: true, method: 'Email', icon: Mail };
  if (prospect.instagram) return { show: true, method: 'Instagram', icon: Instagram };
  if (prospect.contact_phone) return { show: true, method: 'Phone', icon: Phone };
  return { show: true, method: 'Email', icon: Mail };
};

const TYPE_COLORS: Record<string, string> = {
  Hotel: 'bg-blue-100 text-blue-700',
  Gym: 'bg-orange-100 text-orange-700',
  Cafe: 'bg-amber-100 text-amber-800',
  Coworking: 'bg-purple-100 text-purple-700',
  Restaurant: 'bg-rose-100 text-rose-700',
  'Shopping Centre': 'bg-pink-100 text-pink-700',
  Transport: 'bg-cyan-100 text-cyan-700',
  University: 'bg-indigo-100 text-indigo-700',
};

const ProspectCard = ({ prospect, onClick, onDragStart }: ProspectCardProps) => {
  const days = getDaysSince(prospect.last_contacted_at);
  const followUp = getFollowUpRecommendation(prospect);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, prospect.id)}
      onClick={onClick}
      className="bg-card border border-border rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
    >
      {/* Follow-up recommendation banner */}
      {followUp && (
        <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2 -mt-0.5">
          <Bell className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
          <span className="text-[10px] font-semibold text-amber-700">Follow up recommended</span>
          <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
            <followUp.icon className="w-3 h-3" />
            {followUp.method}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-semibold text-sm text-foreground leading-tight">{prospect.name}</h4>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${getRecencyColor(days)}`}>
          {getRecencyLabel(days)}
        </span>
      </div>

      {prospect.type && (
        <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mb-1.5 ${TYPE_COLORS[prospect.type] || 'bg-muted text-muted-foreground'}`}>
          {prospect.type}
        </span>
      )}

      {prospect.address && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1.5">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{prospect.address}</span>
        </div>
      )}

      {prospect.contact_name && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1.5">
          <User className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {prospect.contact_name}
            {prospect.contact_role && ` · ${prospect.contact_role}`}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
        {prospect.assigned_to ? (
          <span className="text-[10px] font-medium bg-secondary/50 text-secondary-foreground px-1.5 py-0.5 rounded-full">
            {prospect.assigned_to}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground">Unassigned</span>
        )}
        <div className="flex gap-1">
          {prospect.website && <Globe className="w-3.5 h-3.5 text-muted-foreground" />}
          {prospect.instagram && <Instagram className="w-3.5 h-3.5 text-muted-foreground" />}
          {prospect.contact_email && <Mail className="w-3.5 h-3.5 text-muted-foreground" />}
          {prospect.notes && <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </div>
    </div>
  );
};

export default ProspectCard;
export type { Prospect };
